import { BLOCK, CLIENT_EVENTS, AWAIT, LOCAL_ACTIONS } from "./agentContract";
import userDetail from "Stores/userDetail";
import { settingsStoreData } from "Stores/settingsData";
import onboardingCopilotStore from "Stores/onboardingCopilotStore";
import { getDomainFromEmail, isFreeMailDomain } from "Utils/emailDomain";
import { MXP_EVENT } from "Static/MixPanelEvents";
import * as svc from "./onboardingServices";

/**
 * The new-user copilot onboarding: a deterministic 10-step flow that turns a
 * signup into an activate-ready campaign, all inside the copilot drawer. Each
 * step is { id, expects(clientEvent, ctx) -> bool, run(api, clientEvent, ctx) }
 * driven by scriptedTransportBase; run() awaits the real service calls in
 * onboardingServices.js. ctx is onboardingCopilotStore.
 *
 * Copy rules (spec): no em-dashes, no exclamation-mark enthusiasm, numbers
 * over adjectives. Loaders are 4-5 rotating messages, never a bare spinner.
 * Onboarding never sends; activation stays on the sequence detail page.
 */

const firstName = () => userDetail?.userInfo?.firstName || "there";

export const signupDomain = () => {
  const brand = userDetail?.brandUrlInfo?.domain || "";
  if (brand) return brand;
  const email =
    settingsStoreData?.userProfileData?.email || userDetail?.userInfo?.email || "";
  return getDomainFromEmail(email);
};

const track = (event, props = {}) => {
  try {
    if (window && window.mixpanel) window.mixpanel.track(event, props);
  } catch (e) {
    /* instrumentation is best-effort */
  }
};

// Prem's decision card (title / body / facts) on a chip row. Body is plain
// text, so strip the **bold** the chat copy uses.
const card = (title, body, facts) => ({
  title,
  body: String(body || "").replace(/\*\*/g, ""),
  ...(facts && facts.length ? { facts } : {})
});

const USE_PROFILE = "ob_use_profile:";
const profileChips = (profiles, max = 3) =>
  (profiles || []).slice(0, max).map((p, i) => ({
    actionId: `${USE_PROFILE}${p.id}`,
    label: `Use ${p.name || "this profile"}${p.active ? " (active)" : ""}`,
    ...(i === 0 ? { style: "primary" } : {})
  }));

const isCta = (evt, actionId) =>
  evt.type === CLIENT_EVENTS.CTA_CLICKED && evt.actionId === actionId;
const isReply = (evt, replyId) =>
  evt.type === CLIENT_EVENTS.QUICK_REPLY_SELECTED && evt.replyId === replyId;

// A 4-5 message rotating loader that runs `work` (a promise) to completion,
// then flips the block to done. Returns whatever `work` resolved to.
const runWork = async (api, turn, { label, messages, detail }, work) => {
  const blockId = turn.openBlock(BLOCK.PROGRESS, { status: "running", label, messages });
  try {
    const result = await work;
    turn.patch(blockId, { status: "done", detail });
    turn.closeBlock(blockId);
    return result;
  } catch (err) {
    turn.patch(blockId, { status: "failed", detail: "That step could not finish." });
    turn.closeBlock(blockId);
    throw err;
  }
};

// Names the step a returning user left on, for the resume prompt.
const STEP_LABELS = {
  1: "what you sell",
  2: "your seller profile",
  3: "who to target",
  4: "your first search",
  5: "your list",
  6: "enrichment",
  7: "connecting your inbox",
  8: "your first campaign"
};

// The four things onboarding does, shown on the welcome and resume cards.
// PLAN_DONE maps the persisted step number to how many of them are behind us.
const ONBOARDING_PLAN = [
  "Read your website and build your seller profile",
  "Find your buyers and save them as a list",
  "Get verified emails and phone numbers",
  "Draft your first campaign for you to review"
];
const PLAN_DONE = { 1: 0, 2: 0, 3: 1, 4: 1, 5: 1, 6: 2, 7: 3, 8: 3 };

const LEVEL_LABEL = { cxo: "C-suite", vp: "VP", director: "Director", manager: "Manager", senior: "Senior" };

// One readable line for the ICP: "{levels} in {departments} at {industries}
// companies, {sizes} employees, {geo}". Empty slots drop out; never a filter
// dump. Falls back to a neutral phrase when nothing is known.
const ACRONYMS = /\b(Vp|Svp|Evp|Avp|Ceo|Cto|Cfo|Coo|Cmo|Cro|Cio|Ciso|Hr|It|Qa|Ux|Ui|Sdr|Bdr|Ae|Rvp|Seo|Sre|Devops)\b/g;
const titleCase = s =>
  String(s)
    .replace(/\b[a-z]/g, c => c.toUpperCase())
    .replace(/\b(Of|And|For)\b/g, m => m.toLowerCase())
    .replace(ACRONYMS, m => m.toUpperCase());

// "51-200, 201-1000, 1001-10000" -> "51 to 10,000"
const sizeSpan = buckets => {
  const nums = (buckets || []).flatMap(b => String(b).split("-").map(n => parseInt(n, 10))).filter(n => n > 0);
  if (!nums.length) return "";
  return `${Math.min(...nums).toLocaleString()} to ${Math.max(...nums).toLocaleString()}`;
};

// The ICP as label/value rows for the confirm card (roles, industries,
// size, geo). Empty slots drop out.
const icpFacts = icp => {
  const i = icp || {};
  const join = (arr, n) => (arr || []).filter(Boolean).slice(0, n).join(", ");
  const titles = (i.title || []).filter(Boolean);
  const who = titles.length
    ? join(titles.map(titleCase), 4) + (titles.length > 4 ? ` +${titles.length - 4}` : "")
    : join((i.level || []).map(l => LEVEL_LABEL[l] || l), 4);
  const rows = [
    { label: "Who", value: who },
    { label: "Industry", value: join(i.industry, 3) },
    { label: "Size", value: sizeSpan(i.employeeCount) ? `${sizeSpan(i.employeeCount)} employees` : "" },
    { label: "Where", value: join(i.country, 3) }
  ];
  return rows.filter(r => r.value);
};

const icpSummary = icp => {
  const i = icp || {};
  const list = (arr, n) => (arr || []).filter(Boolean).slice(0, n).join(", ");
  const titles = (i.title || []).filter(Boolean);
  const who = titles.length
    ? list(titles.map(titleCase), 3) + (titles.length > 3 ? ` and ${titles.length - 3} more` : "")
    : list((i.level || []).map(l => LEVEL_LABEL[l] || l), 3);
  const industries = list(i.industry, 2);
  const sizes = sizeSpan(i.employeeCount);
  const geo = list(i.country, 2);
  const where = [
    industries && `at ${industries} companies`,
    sizes && `${sizes} employees`,
    geo && `in ${geo}`
  ].filter(Boolean);
  const out = [who, where.join(", ")].filter(Boolean).join(" ");
  return out || "people who match your profile";
};

// ── Step 0: welcome (emitted on connect, no client event) ────────────────
export const emitWelcome = async api => {
  const turn = api.beginTurn();
  onboardingCopilotStore.begin("onboarding", userDetail?.userInfo?.memberId);
  onboardingCopilotStore.setStep(0);
  track(MXP_EVENT.ONBOARDING_STARTED);
  turn.addBlock(BLOCK.CTA_CHIPS, {
    title: `Hey ${firstName()}, welcome to Salesgear.`,
    body: "I'm your copilot. Give me a couple of minutes and I'll have your first campaign ready to review.",
    steps: ONBOARDING_PLAN,
    chips: [
      { actionId: "ob_get_started", label: "Let's go", icon: "arrow_forward", style: "primary" },
      { actionId: "ob_dismiss", label: "I'll explore on my own" }
    ]
  });
  turn.end(AWAIT.CTA);
};

// ── Step 2-3: research the seller, save, capture ICP ─────────────────────
async function runProfileStep(api, ctx, website) {
  const turn = api.beginTurn();
  ctx.setStep(2);
  const domain = website || signupDomain();
  let draft;
  try {
    draft = await runWork(
      api,
      turn,
      {
        label: "Building your seller profile",
        messages: [
          `Reading ${domain}...`,
          "Understanding what you sell...",
          "Mapping your market...",
          "Identifying who buys this...",
          "Checking who you compete with..."
        ],
        detail: "Seller profile drafted"
      },
      svc.enrichSeller(website)
    );
  } catch (err) {
    const existing = await Promise.resolve()
      .then(() => svc.listSellerProfiles())
      .catch(() => []);
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card(
        `I couldn't build a profile from ${domain}`,
        existing.length
          ? `You already have ${existing.length} seller profile${existing.length === 1 ? "" : "s"}, so let's use one of those. Or build a new one in the profile builder; I'll pick up from your search when you're back.`
          : "Let's build it together in the profile builder, same two minutes. I'll pick up from your search when you're back."
      ),
      chips: [
        ...profileChips(existing),
        {
          actionId: LOCAL_ACTIONS.NAVIGATE,
          path: "/settings/seller-profiles",
          label: "Open Seller Profile",
          ...(existing.length ? {} : { style: "primary" })
        }
      ]
    });
    turn.end(AWAIT.CTA);
    return;
  }
  ctx.patchArtifacts({ draftProfile: draft });
  await turn.text(`Here's what I understood from ${domain}. Open it to review or edit.`);
  turn.addBlock(BLOCK.SELLER_PROFILE_PREVIEW, { profile: draft, done: false });
  turn.end(AWAIT.SELLER_PROFILE_PREVIEW);
}

// The adjust form. Options matching the derived ICP arrive pre-ticked so the
// user is editing what the copilot already knows, not filling a blank survey.
const ICP_QUESTIONS = derived => {
  const d = derived || {};
  const has = (arr, v) => (arr || []).includes(v);
  const regionOn = label => (REGION_MAP[label] || []).some(c => has(d.country, c));
  return [
    {
      question: "Which seniority levels should I target?",
      header: "Seniority",
      multiSelect: true,
      options: Object.keys(LEVEL_MAP).map(label => ({ label, selected: has(d.level, LEVEL_MAP[label]) }))
    },
    {
      question: "What company sizes fit best?",
      header: "Company size",
      multiSelect: true,
      options: Object.keys(SIZE_MAP).map(label => ({ label, selected: has(d.employeeCount, SIZE_MAP[label]) }))
    },
    {
      question: "Which regions should I search?",
      header: "Geography",
      multiSelect: true,
      options: Object.keys(REGION_MAP).map(label => ({ label, selected: regionOn(label) }))
    }
  ];
};

const LEVEL_MAP = { "C-Suite": "cxo", VP: "vp", Director: "director", Manager: "manager" };
const SIZE_MAP = {
  "1-50": "1-50",
  "51-200": "51-200",
  "201-1000": "201-1000",
  "1000+": "1001-10000"
};
const REGION_MAP = {
  "North America": ["United States", "Canada"],
  Europe: ["United Kingdom", "Germany", "France"],
  "Asia Pacific": ["India", "Australia", "Singapore"],
  Global: []
};

const answersToIcp = (answers, derived) => {
  const pick = header =>
    (answers.find(a => (a.question || "").toLowerCase().includes(header)) || {}).answer || "";
  const list = v => (Array.isArray(v) ? v : String(v || "").split(",").map(s => s.trim()).filter(Boolean));
  const levels = list(pick("seniority")).map(l => LEVEL_MAP[l]).filter(Boolean);
  const sizes = list(pick("size")).map(s => SIZE_MAP[s]).filter(Boolean);
  const regions = list(pick("region")).flatMap(r => REGION_MAP[r] || []);
  return {
    title: derived.title || [],
    level: levels.length ? levels : derived.level || [],
    industry: derived.industry || [],
    employeeCount: sizes.length ? sizes : derived.employeeCount || [],
    country: regions.length ? regions : derived.country || []
  };
};

// Step 1: what are you selling. Shared by the Get-started path and by resume,
// which drops a user back here rather than replaying the Step 0 welcome.
export const emitSellingQuestion = async (api, ctx) => {
  ctx.setStep(1);
  const domain = signupDomain();
  const turn = api.beginTurn();
  // An account that already has seller profiles (an existing user) picks one;
  // building from the website again is the fallback, not the default.
  const existing = await Promise.resolve()
    .then(() => svc.listSellerProfiles())
    .catch(() => []);
  if (existing.length) {
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card(
        "Use a seller profile you already have",
        `You have ${existing.length} seller profile${existing.length === 1 ? "" : "s"}. Pick one and I'll build your buyer list from it, or I can read your website and draft a new one.`,
        [{ label: "Active", value: (existing[0] && existing[0].name) || "none" }]
      ),
      chips: [
        ...profileChips(existing),
        ...(domain && !isFreeMailDomain(domain)
          ? [{ actionId: "ob_use_domain", label: `Draft a new one from ${domain}` }]
          : [{ actionId: "ob_specific_product", label: "Draft a new one from a link" }])
      ]
    });
    turn.end(AWAIT.CTA);
    return;
  }
  if (domain && !isFreeMailDomain(domain)) {
    await turn.text(
      `First, what you sell. Your email says you're at **${domain}**. Should I read that, or is there a specific product page I should use instead?`
    );
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        { actionId: "ob_use_domain", label: `Use ${domain}`, style: "primary" },
        { actionId: "ob_specific_product", label: "It's a specific product" }
      ]
    });
    turn.end(AWAIT.CTA);
    return;
  }
  await turn.text(
    "First, what you sell. Paste a link to your company site, or to the product page that describes it best."
  );
  turn.addBlock(BLOCK.WEBSITE_INPUT, {});
  turn.end(AWAIT.WEBSITE_INPUT);
};

// Resume: never restart from Step 0. Offer to pick up, then jump to the right
// point from the persisted artifacts.
export const emitResume = async api => {
  const turn = api.beginTurn();
  const where = STEP_LABELS[onboardingCopilotStore.step] || "setup";
  turn.addBlock(BLOCK.CTA_CHIPS, {
    title: `Welcome back, ${firstName()}.`,
    body: `You were on ${where}. Pick up where we left off?`,
    steps: ONBOARDING_PLAN,
    doneSteps: PLAN_DONE[onboardingCopilotStore.step] || 0,
    chips: [
      { actionId: "ob_resume", label: "Continue", style: "primary" },
      { actionId: "ob_restart", label: "Start over (keeps what's saved)" }
    ]
  });
  turn.end(AWAIT.CTA);
};

// Step 3: who to reach. Shared by resume and by the off-script re-emit, which
// must return the user to the ICP form and not to the already-approved profile.
export const emitIcpForm = async (api, ctx) => {
  const a = ctx.artifacts || {};
  const derived = a.icpFilters || ctx._icpDerived || (await svc.deriveIcp(a.draftProfile || {}));
  ctx._icpDerived = derived;
  const turn = api.beginTurn();
  await turn.text(
    svc.hasIcp(derived)
      ? "Adjust who to reach. I've ticked what I got from your profile. Change anything, then I'll search."
      : "I couldn't pin down who to reach from your site. Tell me, then I'll search."
  );
  turn.addBlock(BLOCK.QUESTION_FORM, { questions: ICP_QUESTIONS(derived) });
  turn.end(AWAIT.QUESTION_FORM);
};

// Step 3.5: the ICP as a one-line confirmation, not a questionnaire. The
// copilot states who it will target (from the seller intelligence) and asks
// to proceed; the form is only the adjust path.
export const emitIcpConfirm = async (api, ctx, turn) => {
  ctx.setStep(3);
  const icp = (ctx.artifacts && ctx.artifacts.icpFilters) || ctx._icpDerived;
  if (!svc.hasIcp(icp)) return emitIcpForm(api, ctx);
  turn = turn || api.beginTurn();
  turn.addBlock(BLOCK.CTA_CHIPS, {
    title: "Here's who I'll target",
    body: "From your seller profile. Want me to find them?",
    facts: icpFacts(icp),
    chips: [
      { actionId: "ob_icp_go", label: "Yes, find them", icon: "search", style: "primary" },
      { actionId: "ob_icp_adjust", label: "Adjust who to target" }
    ]
  });
  turn.end(AWAIT.CTA);
  return undefined;
};

async function resumeFromArtifacts(api, ctx) {
  // Verify before skipping: ids are hints, the backend is truth. Then recap, so
  // the user sees WHAT is already done instead of being dropped mid-flow into
  // an empty thread with no memory of the profile or list they just made.
  let dropped = [];
  try {
    const checked = await svc.verifyArtifacts(ctx.artifacts || {});
    ctx.patchArtifacts(checked.artifacts);
    dropped = checked.dropped || [];
  } catch (e) {
    /* verification unavailable: fall through on the stored hints */
  }
  const a = ctx.artifacts || {};

  const done = [];
  if (a.savedProfileId && a.draftProfile && a.draftProfile.company_name) {
    done.push(`your ${a.draftProfile.company_name} seller profile is saved`);
  } else if (a.savedProfileId) {
    done.push("your seller profile is saved");
  }
  if (a.listId) {
    done.push(
      a.listSize
        ? `your list ${a.listName || ""} has ${Number(a.listSize).toLocaleString()} contacts`.replace("  ", " ")
        : `your list ${a.listName || ""} is saved`.replace("  ", " ")
    );
  }
  if (done.length) {
    const turn = api.beginTurn();
    let line = `Here's where we are: ${done.join(", and ")}.`;
    if (dropped.includes("emptyList") || dropped.includes("missingList")) {
      line += " The list I saved earlier is empty now, so let's redo that part.";
    }
    await turn.text(line);
    turn.end(null);
  }

  return advanceFromState(api, ctx);
}


// ── the scenario steps ────────────────────────────────────────────────────
// Steps 0-4 (entry through the first search); steps 5-9 are in laterSteps().
const earlySteps = () => [
  {
    id: "resume_pick",
    expects: evt => isCta(evt, "ob_resume"),
    run: async (api, evt, ctx) => resumeFromArtifacts(api, ctx)
  },
  {
    id: "resume_restart",
    expects: evt => isCta(evt, "ob_restart"),
    run: async (api, evt, ctx) => {
      ctx.status = "idle";
      ctx.begin("onboarding", userDetail?.userInfo?.memberId);
      return emitWelcome(api);
    }
  },
  // Step 1: entry / dismissal
  {
    id: "start_or_dismiss",
    expects: evt => isCta(evt, "ob_get_started") || isCta(evt, "ob_dismiss"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_dismiss") {
        ctx.dismiss();
        track(MXP_EVENT.ONBOARDING_DISMISSED, { at: "welcome" });
        const turn = api.beginTurn();
        await turn.text(
          "No problem. I'll be here in the copilot whenever you want to set things up."
        );
        turn.end(AWAIT.USER_INPUT);
        return;
      }
      return emitSellingQuestion(api, ctx);
    }
  },

  {
    id: "choose_product_source",
    expects: evt => isCta(evt, "ob_use_domain") || isCta(evt, "ob_specific_product"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_use_domain") {
        const domain = signupDomain();
        ctx.patchArtifacts({ website: domain });
        track(MXP_EVENT.ONBOARDING_DOMAIN_CONFIRMED, { source: "domain" });
        return runProfileStep(api, ctx, domain);
      }
      const turn = api.beginTurn();
      await turn.text("Paste the link that best describes what you sell.");
      turn.addBlock(BLOCK.WEBSITE_INPUT, {});
      turn.end(AWAIT.WEBSITE_INPUT);
    }
  },

  // Use a seller profile that already exists: from its chip, or from free text
  // such as "use the salesgear" / "use my existing profile". Skips the build
  // and the save; derives the ICP from the profile's intelligence like approve.
  {
    id: "use_existing_profile",
    expects: evt =>
      (evt.type === CLIENT_EVENTS.CTA_CLICKED && String(evt.actionId || "").startsWith(USE_PROFILE)) ||
      (evt.type === CLIENT_EVENTS.USER_MESSAGE &&
        /\b(use|pick|select|choose|go with|take)\b/i.test(evt.text || "") &&
        !/\b(list|sequence|campaign|search)\b/i.test(evt.text || "")),
    run: async (api, evt, ctx) => {
      const profile =
        evt.type === CLIENT_EVENTS.CTA_CLICKED
          ? (await svc.listSellerProfiles()).find(p => `${USE_PROFILE}${p.id}` === evt.actionId)
          : await svc.findSellerProfile(evt.text);
      if (!profile) return false; // not a profile pick: fall through to the consultant
      ctx.setStep(3);
      ctx.patchArtifacts({ savedProfileId: profile.id, draftProfile: profile, website: profile.website || ctx.artifacts.website });
      const turn = api.beginTurn();
      const intel = await runWork(
        api,
        turn,
        {
          label: `Loading ${profile.name || "your profile"}`,
          messages: ["Reading your seller profile...", "Mapping who buys this...", "Preparing your ICP..."],
          detail: "Seller profile ready"
        },
        svc.deriveSellerIntelligence(profile.id).catch(() => null)
      ).catch(() => null);
      track(MXP_EVENT.ONBOARDING_PROFILE_CONFIRMED, { intelligence: !!intel, existing: true });
      const icp = intel ? svc.icpFromIntelligence(intel, profile) : await svc.deriveIcp(profile);
      ctx._icpDerived = icp;
      ctx.patchArtifacts({ icpFilters: icp });
      return emitIcpConfirm(api, ctx, turn);
    }
  },

  {
    id: "website_submitted",
    expects: evt => evt.type === CLIENT_EVENTS.WEBSITE_SUBMITTED,
    run: async (api, evt, ctx) => {
      const website = (evt.website || "").trim();
      ctx.patchArtifacts({ website });
      track(MXP_EVENT.ONBOARDING_DOMAIN_CONFIRMED, { source: "link" });
      return runProfileStep(api, ctx, website);
    }
  },

  // Step 3: profile approved -> save + intelligence + ICP capture
  {
    id: "profile_approved",
    expects: evt => evt.type === CLIENT_EVENTS.SELLER_PROFILE_APPROVED,
    run: async (api, evt, ctx) => {
      const profile = evt.profile || ctx.artifacts.draftProfile || {};
      const turn = api.beginTurn();
      ctx.setStep(3);
      await turn.text(
        "Now building your seller intelligence, the research layer behind every message you send."
      );
      // Save, then generate the seller intelligence (personas, ICP,
      // competitors) in the same loader. The ICP shown next is read off that
      // intelligence; the client heuristic is only the fallback.
      const { saved, intel } = await runWork(
        api,
        turn,
        {
          label: "Building your seller intelligence",
          messages: [
            "Saving your profile...",
            "Indexing your value props...",
            "Mapping who buys this...",
            "Preparing your ICP..."
          ],
          detail: "Seller intelligence ready. Your profile lives in Settings, edit it any time."
        },
        (async () => {
          const s = await svc.saveSeller(profile);
          // Record the id the moment the profile lands. Deriving intelligence
          // takes minutes, and leaving during it used to lose the id entirely:
          // resume then re-asked for approval and saved a second profile.
          if (s && s.id) ctx.patchArtifacts({ savedProfileId: s.id });
          const i = s && s.id ? await svc.deriveSellerIntelligence(s.id).catch(() => null) : null;
          return { saved: s, intel: i };
        })()
      ).catch(() => ({ saved: null, intel: null }));
      // Only widen, never clear: the id was already recorded above, and the
      // failure path here used to blank it out again.
      if (saved && saved.id) ctx.patchArtifacts({ savedProfileId: saved.id });
      track(MXP_EVENT.ONBOARDING_PROFILE_CONFIRMED, { intelligence: !!intel });
      const icp = intel ? svc.icpFromIntelligence(intel, profile) : await svc.deriveIcp(profile);
      ctx._icpDerived = icp;
      ctx.patchArtifacts({ icpFilters: icp });
      return emitIcpConfirm(api, ctx, turn);
    }
  },

  // Step 3.5: confirm or adjust the derived ICP
  {
    id: "icp_go",
    expects: evt => isCta(evt, "ob_icp_go"),
    run: async (api, evt, ctx) => runSearchStep(api, ctx, ctx.artifacts.icpFilters || ctx._icpDerived || {})
  },
  {
    id: "icp_adjust",
    expects: evt => isCta(evt, "ob_icp_adjust"),
    run: async (api, evt, ctx) => emitIcpForm(api, ctx)
  },

  {
    id: "profile_discarded",
    expects: evt => evt.type === CLIENT_EVENTS.SELLER_PROFILE_DISCARDED,
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await turn.text(
        "No problem. Paste a different link and I'll research it again, or build the profile yourself in Settings."
      );
      turn.addBlock(BLOCK.WEBSITE_INPUT, {});
      turn.end(AWAIT.WEBSITE_INPUT);
      return false; // allow another website submission to re-enter
    }
  },

  // Step 4-5: search + save list
  {
    id: "icp_submitted",
    expects: evt => evt.type === CLIENT_EVENTS.QUESTION_FORM_RESPONSE,
    run: async (api, evt, ctx) => {
      const derived = ctx._icpDerived || ctx.artifacts.icpFilters || { industry: [], country: [] };
      const icp = answersToIcp(evt.answers || [], derived);
      return runSearchStep(api, ctx, icp);
    }
  }
];

// Step 4: the first search. Shared by the ICP confirmation ("Yes, find them")
// and the adjust form, so both paths run the identical search + save prompt.
async function runSearchStep(api, ctx, icp) {
      ctx.patchArtifacts({ icpFilters: icp });
      ctx.setStep(4);
      const turn = api.beginTurn();
      await turn.text(`Now your buyers. Searching for ${icpSummary(icp)}.`);
      let result = await runWork(
        api,
        turn,
        {
          label: "Searching your ICP",
          messages: [
            "Matching titles and seniority...",
            "Filtering by company size...",
            "Checking regions...",
            "Ranking by fit..."
          ],
          detail: "Search done"
        },
        svc.runSearch(icp)
      ).catch(() => ({ total: 0, rows: [] }));
      if ((result.total || 0) < 10) {
        const narrow = result.total || 0;
        const loosened = await svc.loosenSearch(icp).catch(() => null);
        if (loosened && loosened.dropped) {
          result = loosened;
          await turn.text(
            `Only ${narrow} matched, so I widened the ${loosened.dropped} filter and found ${(result.total || 0).toLocaleString()}. You can narrow it back any time.`
          );
        }
      }
      ctx.patchArtifacts({ searchTotal: result.total || 0 });
      // Snapshot the lists as they are RIGHT NOW, server-side. A detour (the
      // agent saving a list, the user doing it in another tab) is otherwise
      // invisible after a reload: the in-memory snapshot beginDetour takes dies
      // with the page, so resume replayed "save these as your first list" over
      // a list the account already had.
      svc
        .snapshotLists()
        .then(lists => ctx.patchArtifacts({ searchAt: Date.now(), listsAtSearch: (lists || []).slice(0, 60) }))
        .catch(() => {});
      track(MXP_EVENT.ONBOARDING_SEARCH_DONE, { total: result.total || 0 });
      // Nothing matched, even after loosening. An empty table and an offer to
      // "save these as your first list" is nonsense with zero rows, so end the
      // step on the two things that can actually move it forward.
      if (!(result.total || 0)) {
        await turn.text(
          "Nothing matched that profile, even after widening it. The targeting is usually too narrow when this happens, so let's change who we look for or refine it in the list builder."
        );
        turn.addBlock(BLOCK.CTA_CHIPS, {
          chips: [
            { actionId: "ob_icp_adjust", label: "Adjust who to target", style: "primary" },
            { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/list-builder", label: "Refine in the list builder" }
          ]
        });
        turn.end(AWAIT.CTA);
        return undefined;
      }
      const shown = Math.min((result.rows || []).length, 25);
      const total = result.total || 0;
      await turn.text(
        `**${(result.total || 0).toLocaleString()}** people match your ICP. Here are the first ${shown}.`
      );
      // The standard prospect table (opens the shared side panel as a popup in
      // the drawer). Served by the transport's fetchTablePage from the loaded
      // results, so it looks and behaves like the discover search.
      turn.addBlock(BLOCK.TABLE_REF, {
        tableId: "ob_prospects",
        title: "People matching your ICP",
        // Full match total: the table pages the live search in view mode, so it
        // spans every matching record, not just the first page shown in chat.
        totalCount: total,
        // The honest "of N matched" the card shows.
        matchTotal: result.total || 0,
        selectable: true,
        selectionScope: "page",
        // ContactActionBar denies every action unless the table names it, so
        // selection alone rendered a bar with nothing to do. Prospect rows are
        // not owned yet, so add-to-list is the only one that applies.
        actions: ["add_to_list"],
        columns: [
          { key: "name", label: "Name" },
          { key: "title", label: "Title" },
          { key: "company", label: "Company" },
          { key: "industry", label: "Industry" },
          { key: "location", label: "Location" }
        ]
      });
      // The TABLE_REF card above already carries its own "View", so no separate
      // "View the list" chip here (it was a duplicate control).
      // A single save adds up to 100 (the copilot/list-save contract). When the
      // search has more, say so and let the user add the top 100 now.
      const overCap = total > svc.LIST_SAVE_CAP;
      if (overCap) {
        await turn.text(
          `You can add up to ${svc.LIST_SAVE_CAP} contacts to a list at a time. I'll save the top ${svc.LIST_SAVE_CAP} of these ${total.toLocaleString()} now, and you can add more later.`
        );
      }
      turn.addBlock(BLOCK.QUICK_REPLIES, {
        prompt: "Want me to save these as your first list?",
        replyId: "save_list",
        options: [
          {
            value: "save",
            label: overCap ? `Add top ${svc.LIST_SAVE_CAP} to list` : "Add to list"
          },
          { value: "refine", label: "Refine the search first" }
        ]
      });
      turn.end(AWAIT.QUICK_REPLY);
}

// Steps 5-9 continue the scenario list started above.
const laterSteps = () => [
  {
    id: "save_or_refine",
    expects: evt => isReply(evt, "save_list"),
    run: async (api, evt, ctx) => {
      if (evt.value === "refine") {
        // Refine in the list builder. The copilot docks into the side panel
        // and cannot see what the user does there, so snapshot the lists now
        // and offer two ways back: they already added to a list (detect it),
        // or save whatever the refined search currently shows.
        ctx.patchArtifacts({ refineAt: Date.now(), refineLists: await svc.snapshotLists() });
        const turn = api.beginTurn();
        turn.addBlock(BLOCK.CTA_CHIPS, {
          ...card(
            "Refine in the list builder",
            "I'll stay in the side panel and follow along: each time you search I'll show the new match count and offer to save it. If you add the contacts to a list there instead, just tell me."
          ),
          chips: [
            { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/list-builder", label: "Open list builder", style: "primary" },
            { actionId: "ob_refine_added", label: "I've added them to a list" },
            { actionId: "ob_refine_save", label: "Save the current results" }
          ]
        });
        turn.end(AWAIT.CTA);
        watchRefine(api, ctx);
        return false; // stay available so a later save can proceed
      }
      return saveListAndWait(api, ctx, ctx.artifacts.searchTotal);
    }
  },

  // Back from refining. Either pick up the list the user added to in the list
  // builder, or save the refined search's current results.
  {
    id: "refine_return",
    expects: evt => isCta(evt, "ob_refine_added") || isCta(evt, "ob_refine_save"),
    run: async (api, evt, ctx) => {
      stopRefineWatch();
      if (evt.actionId === "ob_refine_save") {
        const total = svc.currentSearchTotal() || ctx.artifacts.searchTotal;
        ctx.patchArtifacts({ searchTotal: total });
        return saveListAndWait(api, ctx, total);
      }
      const hit = await svc.findListChangedSince(ctx.artifacts.refineLists, ctx.artifacts.refineAt);
      if (!hit) {
        const turn = api.beginTurn();
        await turn.text(
          "I don't see a list that changed since you started refining. Add the contacts to a list in the list builder, then tell me again, or I can save the current results."
        );
        turn.addBlock(BLOCK.CTA_CHIPS, {
          chips: [
            { actionId: "ob_refine_added", label: "I've added them to a list", style: "primary" },
            { actionId: "ob_refine_save", label: "Save the current results" }
          ]
        });
        turn.end(AWAIT.CTA);
        return false;
      }
      ctx.setStep(5);
      ctx.patchArtifacts({ listId: hit.id, listName: hit.name, listSize: hit.count });
      track(MXP_EVENT.ONBOARDING_LIST_SAVED, { source: "refine" });
      const turn = api.beginTurn();
      await turn.text(`Found your list **${hit.name}** with ${hit.count.toLocaleString()} contacts. Let's continue from there.`);
      return emitWaitForFill(api, ctx, turn);
    }
  },

  // List save failed: retry with a fresh unique name, or skip to enrich.
  {
    id: "savelist_retry",
    expects: evt => isCta(evt, "ob_savelist_retry") || isCta(evt, "ob_savelist_skip"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_savelist_skip") {
        const turn = api.beginTurn();
        await turn.text("No problem, we can save the list later. Let's keep going.");
        turn.end(AWAIT.USER_INPUT);
        return emitConnectStep(api, ctx);
      }
      ctx.setStep(5);
      const turn = api.beginTurn();
      const company = userDetail?.userInfo?.companyName || ctx.artifacts.website || "";
      const base = company ? `${company} ICP prospects` : "ICP prospects";
      const listName = `${base} ${Date.now().toString().slice(-4)}`;
      const saved = await runWork(
        api,
        turn,
        {
          label: "Saving your list",
          messages: ["Creating your list...", "Adding matched prospects...", "Finishing up..."],
          detail: "List saved"
        },
        svc.saveList(listName, null, ctx.artifacts.searchTotal)
      ).catch(() => null);
      if (!saved || !saved.ok || !saved.listId) {
        await turn.text(
          "Still couldn't save the list. Let's move on for now, you can save it from the list builder later."
        );
        turn.end(AWAIT.USER_INPUT);
        return emitConnectStep(api, ctx);
      }
      ctx.patchArtifacts({ listId: saved.listId, listName: saved.listName, listSize: saved.listSize || 0 });
      track(MXP_EVENT.ONBOARDING_LIST_SAVED, { retried: true });
      await turn.text(
        `Saved as **${saved.listName}**.${saved.listSize ? ` Adding up to ${saved.listSize.toLocaleString()} contacts in the background.` : ""}`
      );
      return emitWaitForFill(api, ctx, turn);
    }
  },

  // The user hand-picked prospects in the popup table and clicked "Add to
  // list". Add exactly those, then continue the flow into enrich, the same as
  // the scripted "Add to list" quick reply would.
  {
    id: "prospects_added_via_panel",
    expects: evt => evt.type === CLIENT_EVENTS.ADD_PROSPECTS_TO_LIST,
    run: async (api, evt, ctx) => {
      ctx.setStep(5);
      const rows = evt.rows || [];
      const count = rows.length;
      // Close the popup right away, then show a loader in the copilot while the
      // add runs -- rather than freezing the open popup with no feedback.
      closeTablePopup();
      const turn = api.beginTurn();
      const result = await runWork(
        api,
        turn,
        {
          label: `Adding ${count.toLocaleString()} ${count === 1 ? "contact" : "contacts"} to your list`,
          messages: ["Saving your selection...", "Updating your list...", "Finishing up..."],
          detail: "Contacts added"
        },
        svc.addRowsToList(evt.listId, rows)
      ).catch(() => ({ added: 0, skipped: count }));
      // Patch the table block so the shipped toast reports the real numbers.
      if (evt.blockId) {
        turn.patch(evt.blockId, {
          listResult: { added: result.added || 0, skipped: result.skipped || 0, listId: evt.listId }
        });
      }
      ctx.patchArtifacts({
        listId: evt.listId || "",
        listName: svc.getListName(evt.listId) || ctx.artifacts.listName || "",
        listSize: result.added || count
      });
      track(MXP_EVENT.ONBOARDING_LIST_SAVED, { source: "panel" });
      await turn.text(
        `Added ${(result.added || count).toLocaleString()} contacts to your list. Let's keep going.`
      );
      turn.end(AWAIT.USER_INPUT);
      return emitConnectStep(api, ctx);
    }
  },

  // Step 5.5: the background bulk job fills the list. Enriching or enrolling an
  // empty list is nonsense, so gate on the fill before enrich. The user can ask
  // for status, and a bounded auto-poll reports completion in the same chat.
  {
    id: "check_fill",
    expects: evt =>
      isCta(evt, "ob_check_fill") ||
      (evt.type === CLIENT_EVENTS.USER_MESSAGE &&
        /\b(is it|are they|status|done|ready|finished|added|how long)\b/i.test(evt.text || "")),
    // Stays available (run returns false until the fill is done) so the user
    // can check several times before it finishes.
    run: async (api, evt, ctx) => reportFillStatus(api, ctx)
  },

  // Step 6: enrich choice
  {
    id: "enrich_choice",
    expects: evt =>
      isCta(evt, "ob_enrich_email") || isCta(evt, "ob_enrich_both") || isCta(evt, "ob_enrich_skip"),
    run: async (api, evt, ctx) => {
      ctx.setStep(6);
      if (evt.actionId === "ob_enrich_skip") {
        const turn = api.beginTurn();
        await turn.text("No problem. I'll add the contacts as they are; anyone without a verified email is skipped by email steps.");
        turn.end(AWAIT.USER_INPUT);
        return emitEnrol(api, ctx);
      }
      const type = evt.actionId === "ob_enrich_both" ? "EMAIL_PHONE" : "EMAIL";
      ctx.patchArtifacts({ enrichChoice: type });
      return runEnrich(api, ctx, type);
    }
  },

  // After the sequence exists: the list-fill check and the skip-to-review exit.
  {
    id: "post_sequence",
    expects: evt => isCta(evt, "ob_check_fill_post") || isCta(evt, "ob_skip_to_review"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_skip_to_review") {
        await emitFinalCard(api, ctx, { skipped: true });
        return false;
      }
      await emitPostSequence(api, ctx);
      return false;
    }
  },

  // Enrichment is a background job: report its real status (done, running,
  // failed) and offer the right next move. Stays available.
  {
    id: "enrich_status",
    expects: evt =>
      isCta(evt, "ob_enrich_status") ||
      (evt.type === CLIENT_EVENTS.USER_MESSAGE &&
        /\benrich/i.test(evt.text || "") &&
        /\b(done|status|finished|complete|ready)\b/i.test(evt.text || "")),
    run: async (api, evt, ctx) => reportEnrichStatus(api, ctx)
  },

  // After an off-script answer: ask more, or return to the rail.
  {
    id: "offscript_bridge",
    expects: evt => isCta(evt, "ob_ob_continue") || isCta(evt, "ob_ask_more"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_ask_more") {
        const turn = api.beginTurn();
        await turn.text("Go ahead, ask away. I'll bring us back to the setup after.");
        turn.end(AWAIT.USER_INPUT);
        return false;
      }
      if (!(await reconcileAfterDetour(api, ctx))) await advanceFromState(api, ctx);
      return false; // reusable: every off-script detour ends here
    }
  },

  // Enrich failed: retry the same choice, or continue without enriching.
  {
    id: "enrich_retry",
    expects: evt => isCta(evt, "ob_enrich_retry") || isCta(evt, "ob_enrich_continue"),
    run: async (api, evt, ctx) => {
      if (evt.actionId === "ob_enrich_continue") {
        const turn = api.beginTurn();
        await turn.text("No problem, we can enrich from the list later. Let's keep going.");
        turn.end(AWAIT.USER_INPUT);
        return emitEnrol(api, ctx);
      }
      return runEnrich(api, ctx, ctx.artifacts.enrichChoice || "EMAIL");
    }
  },

  // Step 7: connect (loops on re-check, stays available)
  {
    id: "connect_check",
    expects: evt => isCta(evt, "ob_check_connection"),
    run: async (api, evt, ctx) => {
      const status = await svc.refreshConnectStatus();
      ctx.patchArtifacts({ emailConnected: status.email, linkedInConnected: status.linkedIn });
      if (status.email) {
        track(MXP_EVENT.ONBOARDING_INBOX_CONNECTED);
        return emitCampaignStep(api, ctx);
      }
      const turn = api.beginTurn();
      await turn.text(
        "I don't see a connected inbox yet. Connect your email in Settings, then choose Check connection."
      );
      turn.end(AWAIT.CTA);
      return false;
    }
  },

  {
    id: "connect_later",
    expects: evt => isCta(evt, "ob_connect_later"),
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await turn.text(
        "No problem. Your profile and list are saved here. Campaigns need a connected inbox, so when yours is connected, reopen the copilot and choose Check connection."
      );
      turn.end(AWAIT.CTA);
    }
  },

  // Step 8: campaign style choice. The style cards themselves are the buttons
  // (COMPARISON_GRID cta), so the choice arrives as a user message.
  {
    id: "campaign_choice",
    expects: evt =>
      evt.type === CLIENT_EVENTS.USER_MESSAGE &&
      /create the .*campaign|connect linkedin to continue/i.test(evt.text || ""),
    run: async (api, evt, ctx) => {
      const text = (evt.text || "").toLowerCase();
      if (/connect linkedin/.test(text)) {
        const turn = api.beginTurn();
        await turn.text(
          "LinkedIn connects through the Salesgear Chrome extension. Connect it in Settings, then pick a LinkedIn style here."
        );
        turn.addBlock(BLOCK.CTA_CHIPS, {
          chips: [
            {
              actionId: LOCAL_ACTIONS.NAVIGATE,
              path: "/settings/account",
              label: "Connect LinkedIn",
              style: "primary"
            }
          ]
        });
        turn.end(AWAIT.USER_INPUT);
        return false; // stay available so a style can still be picked
      }
      const status = await svc.refreshConnectStatus();
      if (!status.email) {
        const turn = api.beginTurn();
        await turn.text("Connect your email inbox first, then I can build the campaign.");
        turn.end(AWAIT.USER_INPUT);
        return false;
      }
      ctx.setStep(9);
      const deep = /deep research/.test(text);
      const linkedIn = /linkedin/.test(text);
      // Onboarding does not write the steps itself. Hand off to the shipped
      // "build a sequence with Copilot" flow, which reads the saved seller
      // profile, creates the sequence with steps, shows them, and offers open /
      // add-contacts against the saved list. Contacts still require a connected
      // inbox (Step 7 gate) before that flow can enrol them.
      const style = deep
        ? "a deep research email and LinkedIn sequence"
        : linkedIn
          ? "an email and LinkedIn sequence"
          : "a 3-step email sequence";
      track(MXP_EVENT.ONBOARDING_CAMPAIGN_CREATED, { deep, linkedIn });
      const turn = api.beginTurn();
      await turn.text(
        `Good choice. I'll build ${style} from your seller profile now. Once it's ready we'll get verified emails and add your list. Nothing goes out until you activate it.`
      );
      turn.end(AWAIT.USER_INPUT);
      ctx.patchArtifacts({ campaignStyle: style });
      // Swap to the live copilot's build-sequence flow after this turn settles.
      // It creates and shows the sequence; the script resumes once the
      // sequence id lands (copilotStore.returnToOnboardingAfterSequence).
      handoffToSequenceBuilder(
        `${sequenceBrief(ctx)}Build ${style} from my seller profile. Only create the sequence and show it. Do not add or enrol contacts, do not enrich, and do not activate it; stop as soon as the sequence is created.`
      );
    }
  }
];

const buildOnboardingScenario = () => [...earlySteps(), ...laterSteps()];

// Everything onboarding already asked the user, restated for the live
// sequence builder. Without it the build flow re-asked sequence type, audience
// and goal, and never learned about the list saved at Step 5.
function sequenceBrief(ctx) {
  const a = ctx.artifacts || {};
  const icp = a.icpFilters || {};
  const p = a.draftProfile || {};
  const bits = [];
  const join = v => (Array.isArray(v) ? v.filter(Boolean).join(", ") : "");
  const levels = join(icp.level);
  const sizes = join(icp.employeeCount);
  const regions = join(icp.country);
  const audience = [levels && `${levels} level`, sizes && `at ${sizes} employee companies`, regions && `in ${regions}`]
    .filter(Boolean)
    .join(" ");
  if (audience) bits.push(`Audience: ${audience}.`);
  const goal = String(p.goal_of_outreach || "").trim().replace(/[.\s]+$/, "");
  if (goal) bits.push(`Goal: ${goal}.`);
  if (a.listName) bits.push(`Enrol contacts from my list "${a.listName}".`);
  else if (a.listId) bits.push("Enrol the contacts from the list I just saved.");
  // Pin the seller profile just created in this session, so the build uses IT
  // (e.g. the Jenkins profile) rather than picking an older workspace profile.
  if (a.savedProfileId) {
    bits.push(
      `Use the seller profile I just created (seller_profile_id ${a.savedProfileId}); do not use any other profile.`
    );
  }
  if (!bits.length) return "";
  // Onboarding never activates for the user: create the sequence, enrol the
  // list, then stop and point them to the sequence page to review and activate.
  bits.push("Do not activate or start the sequence.");
  return `I already answered these during setup, so use them and skip the setup questions. ${bits.join(" ")} `;
}

// Closes the shared prospect table popup (lazy require avoids a circular
// import: copilotStore -> AgentTransport -> OnboardingAgentTransport -> here).
function closeTablePopup() {
  try {
    // eslint-disable-next-line global-require
    require("Stores/copilotStore").default.closeTablePanel();
  } catch (e) {
    /* panel may already be closed */
  }
}

// Hands the campaign build to the live copilot's shipped build-sequence flow.
// Deferred a tick so the onboarding turn finishes rendering before the
// transport is swapped underneath it. Lazy require avoids the circular import.
function handoffToSequenceBuilder(triggerText) {
  setTimeout(() => {
    try {
      // eslint-disable-next-line global-require
      require("Stores/copilotStore").default.handoffToLiveSequenceBuilder(triggerText);
    } catch (e) {
      /* best-effort: the user can still open the sequence builder manually */
    }
  }, 400);
}

// Step 6 emitter (enrich, cost shown before any deduction). Reused by the
// scripted save and by the popup add-to-list path so both continue the same
// way. Writes into the turn it is given.
// Runs the chosen enrichment, reports the outcome, and on failure lets the
// user see why, retry, or continue the flow. Enrichment itself continues in
// the background on the server; here we surface whether it STARTED cleanly.
async function runEnrich(api, ctx, type) {
  const turn = api.beginTurn();
  const label = type === "EMAIL_PHONE" ? "Enriching emails and phones" : "Enriching emails";
  const res = await runWork(
    api,
    turn,
    {
      label,
      messages: ["Verifying details...", "Updating your list...", "Finishing up..."],
      detail: "Enrichment started"
    },
    svc.enrichContacts(type, ctx.artifacts.listId)
  ).catch(err => ({ ok: false, message: (err && err.message) || "Enrichment failed" }));
  track(MXP_EVENT.ONBOARDING_ENRICHED, { type, ok: !!(res && res.ok) });
  if (res && res.ok) {
    // A background BULK_PURCHASE job: track it rather than assume it worked.
    ctx.patchArtifacts({ enrichStartedAt: Date.now(), enrichChoice: type });
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card(
        "Enrichment is running",
        "It runs in the background. Anyone without a verified email stays out of the campaign. Check on it here, or keep going and I'll report back before we enrol.",
        [
          { label: "List", value: ctx.artifacts.listName || "your list" },
          { label: "Finding", value: type === "EMAIL_PHONE" ? "emails and phone numbers" : "verified emails" }
        ]
      ),
      chips: [
        { actionId: "ob_enrich_status", label: "Check enrichment", style: "primary" },
        { actionId: "ob_enrich_continue", label: "Add contacts while it runs" }
      ]
    });
    turn.end(AWAIT.CTA);
    return undefined;
  }
  // Out of credits (server returns 200 with statusCode 409): say how many
  // are needed vs left, and offer to top up or skip and continue.
  if (res && res.reason === "insufficient_credits") {
    const need = ctx.artifacts.listSize || 0;
    const have = res.remaining || 0;
    await turn.text(
      `You need ${need ? `about ${need.toLocaleString()} ` : ""}credits to enrich this list but have ${have.toLocaleString()} left. Top up your credits, or skip and enrich later.`
    );
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/pricing", label: "Top up credits", style: "primary" },
        { actionId: "ob_enrich_continue", label: "Skip for now" }
      ]
    });
    turn.end(AWAIT.CTA);
    return undefined;
  }
  // Other failure: say why plainly and let the user retry or move on.
  const why = res && res.message ? ` (${res.message})` : "";
  await turn.text(`Enrichment couldn't start${why}. Want me to try again, or continue and enrich from the list later?`);
  turn.addBlock(BLOCK.CTA_CHIPS, {
    chips: [
      { actionId: "ob_enrich_retry", label: "Try again", style: "primary" },
      { actionId: "ob_enrich_continue", label: "Continue for now" }
    ]
  });
  turn.end(AWAIT.CTA);
  return undefined;
}

// While the user refines in the list builder, follow their searches: each new
// result total gets one message with the count and a save offer, so they are
// never left wondering what to do next. One watcher at a time; stopped when
// they come back (refine_return) or leave the flow.
let _refineWatch = null;
export const stopRefineWatch = () => {
  if (_refineWatch) {
    _refineWatch();
    _refineWatch = null;
  }
};

function watchRefine(api, ctx) {
  stopRefineWatch();
  let last = ctx.artifacts.searchTotal || 0;
  _refineWatch = svc.watchSearchTotal(async total => {
    if (!total || total === last) return;
    last = total;
    ctx.patchArtifacts({ searchTotal: total });
    const cap = svc.LIST_SAVE_CAP;
    const turn = api.beginTurn();
    await turn.text(
      `Your refined search now matches **${total.toLocaleString()}** people.${
        total > cap ? ` I can save the top ${cap} as your list, or keep refining.` : " Want me to save them as your list?"
      }`
    );
    turn.addBlock(BLOCK.CTA_CHIPS, {
      chips: [
        { actionId: "ob_refine_save", label: total > cap ? `Add top ${cap} to list` : "Add to list", style: "primary" },
        { actionId: "ob_refine_added", label: "I've added them to a list" }
      ]
    });
    turn.end(AWAIT.CTA);
  });
}

// Step 5: save the matched (or refined) search as the first list, then wait
// for the background fill. Shared by the scripted save and the refine return.
async function saveListAndWait(api, ctx, total) {
  ctx.setStep(5);
  const turn = api.beginTurn();
  const company = userDetail?.userInfo?.companyName || ctx.artifacts.website || "";
  const listName = company ? `${company} ICP prospects` : "ICP prospects";
  ctx.patchArtifacts({ fillStartedAt: Date.now() });
  const saved = await runWork(
    api,
    turn,
    {
      label: "Saving your list",
      messages: ["Creating your list...", "Adding matched prospects...", "Finishing up..."],
      detail: "List saved"
    },
    svc.saveList(listName, null, total)
  ).catch(() => null);
  // The list was created but no contacts could be added: out of credits. Say
  // so and offer to top up or carry on, instead of a wait gate that never ends.
  if (saved && saved.reason === "insufficient_credits") {
    ctx.patchArtifacts({ listId: saved.listId, listName: saved.listName, listSize: 0 });
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card(
        "Not enough credits to add contacts",
        `I created ${saved.listName} but couldn't add contacts to it. Top up your credits and I'll add them, or continue and add contacts later.`,
        [{ label: "List", value: saved.listName }]
      ),
      chips: [
        { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/pricing", label: "Top up credits", style: "primary" },
        { actionId: "ob_savelist_retry", label: "Try adding again" },
        { actionId: "ob_savelist_skip", label: "Continue without them" }
      ]
    });
    turn.end(AWAIT.CTA);
    return false;
  }
  // Only continue when the list actually saved. On failure, say so and let
  // the user retry (a fresh unique name) rather than silently moving on.
  if (!saved || !saved.ok || !saved.listId) {
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card("Couldn't save the list", "The name may already be in use. Want me to try again with a fresh name?"),
      chips: [
        { actionId: "ob_savelist_retry", label: "Try again", style: "primary" },
        { actionId: "ob_savelist_skip", label: "Skip for now" }
      ]
    });
    turn.end(AWAIT.CTA);
    return false; // stay available for the retry
  }
  const listSize = saved.listSize || 0;
  const finalName = saved.listName || listName;
  ctx.patchArtifacts({ listId: saved.listId, listName: finalName, listSize });
  track(MXP_EVENT.ONBOARDING_LIST_SAVED);
  await turn.text(
    `Saved as **${finalName}**.${listSize ? ` Adding up to ${listSize.toLocaleString()} contacts in the background.` : ""}`
  );
  return emitWaitForFill(api, ctx, turn);
}

// ── Step 5.5: wait for the background list-fill before enrich ──────────────
// Enriching or enrolling an empty list is nonsense, so after the save the flow
// waits here until the background bulk job has filled the list. Status is
// user-driven (Check status / "is it done?"): pull the count on demand, and
// only move on to enrich once the contacts are in. Nothing polls in the
// background, so nothing runs after the user leaves the chat.
// Enrichment is asked AFTER the sequence exists (it needs the list filled and
// the campaign known), so a filled list moves on to connecting the inbox.
async function advanceToEnrich(api, ctx) {
  if (ctx._fillResolved) return undefined;
  ctx._fillResolved = true;
  const turn = api.beginTurn();
  const n = ctx.artifacts.listSize || 0;
  await turn.text(`Your contacts are in the list${n ? ` (${n.toLocaleString()} added)` : ""}. Next, your inbox.`);
  turn.end(AWAIT.USER_INPUT);
  return emitConnectStep(api, ctx);
}

// A handful of contacts land in seconds, so waiting for them is cheap and the
// user gets to enrich straight away. A big list takes minutes, and blocking the
// whole flow on it is what made onboarding feel stuck: say it's running, carry
// on to the campaign, and let the sequence builder gate on the contacts later.
const FILL_FAST_MAX = 25;
const FILL_POLL_MS = 2000;
const FILL_POLL_TRIES = 4;

async function emitWaitForFill(api, ctx, turn) {
  turn = turn || api.beginTurn();
  ctx._fillResolved = false;
  ctx._lastFill = -1;
  const listName = ctx.artifacts.listName || "your list";
  const expected = ctx.artifacts.listSize || 0;

  if (expected && expected <= FILL_FAST_MAX) {
    const st = await runWork(
      api,
      turn,
      {
        label: "Adding your contacts",
        messages: ["Adding contacts to the list...", "Almost there..."],
        detail: "Contacts added"
      },
      pollFill(ctx.artifacts.listId, expected)
    ).catch(() => null);
    if (st && st.added > 0) {
      ctx._fillResolved = true;
      ctx.patchArtifacts({ listSize: st.added });
      await turn.text(`All ${st.added.toLocaleString()} contacts are in **${listName}**. Next, your inbox.`);
      turn.end(AWAIT.USER_INPUT);
      return emitConnectStep(api, ctx);
    }
    // Slower than expected: fall through to the manual status gate.
  } else if (expected) {
    ctx.patchArtifacts({ fillDeferred: true, enrichChoice: "deferred" });
    await turn.text(
      `I'm adding ${expected.toLocaleString()} contacts to **${listName}** in the background, which takes a few minutes. No need to wait on it: let's set up your campaign now, and we'll enrol them once they've landed.`
    );
    return emitConnectStep(api, ctx);
  }

  turn.addBlock(BLOCK.CTA_CHIPS, {
    ...card(
      "Adding contacts to your list",
      "This runs in the background. I'll keep an eye on it; ask me for the status any time and I'll tell you when they're in so we can enrich and enrol them.",
      [
        { label: "List", value: listName },
        { label: "Contacts", value: `up to ${expected.toLocaleString()}` }
      ]
    ),
    chips: [{ actionId: "ob_check_fill", label: "Check status", style: "primary" }]
  });
  turn.end(AWAIT.CTA);
}

// Poll the list count until it reaches the cap or stops moving.
async function pollFill(listId, expected) {
  let last = -1;
  let st = null;
  for (let i = 0; i < FILL_POLL_TRIES; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    st = await svc.getListFillStatus(listId, expected);
    if (st.done || (st.added > 0 && st.added === last)) return st;
    last = st.added;
    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => setTimeout(r, FILL_POLL_MS));
  }
  return st;
}

// User asked "is it done?" (or clicked Check status). Pull the fill status from
// the list count and report done / percent. When done, advance to enrich.
async function reportFillStatus(api, ctx) {
  const st = await svc.getListFillStatus(ctx.artifacts.listId, ctx.artifacts.listSize, ctx.artifacts.fillStartedAt);
  // The server job failed: say so and offer a retry, never "still adding".
  if (st.failed) {
    const turn = api.beginTurn();
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card(
        "Adding contacts failed",
        `The background job for **${ctx.artifacts.listName || "your list"}** stopped before it finished${st.added ? `; ${st.added.toLocaleString()} made it in` : ""}. I can run it again, or we can continue with what's there.`
      ),
      chips: [
        { actionId: "ob_savelist_retry", label: "Try adding again", style: "primary" },
        { actionId: "ob_savelist_skip", label: "Continue anyway" }
      ]
    });
    turn.end(AWAIT.CTA);
    return undefined;
  }
  // Done when the job reports finished, the count reaches the cap, or plateaus
  // above zero (skips mean it can settle below the cap and never reach it).
  const plateau = st.added > 0 && st.added === ctx._lastFill;
  ctx._lastFill = st.added;
  if (st.done || plateau) {
    await advanceToEnrich(api, ctx);
    return undefined;
  }
  const turn = api.beginTurn();
  await turn.text("Still adding contacts. A little longer; ask me again any time, or keep going and I'll pick this up when it's done.");
  turn.addBlock(BLOCK.CTA_CHIPS, {
    title: "Progress",
    facts: [{ label: "Added", value: `${st.added.toLocaleString()} of ${(st.expected || 0).toLocaleString()} (${st.percent}%)` }],
    chips: [{ actionId: "ob_check_fill", label: "Check status", style: "primary" }]
  });
  turn.end(AWAIT.CTA);
  return false; // stay available for the next check
}

// "Is enrichment done?": read the BULK_PURCHASE job fired at enrichStartedAt.
async function reportEnrichStatus(api, ctx) {
  const job = await svc.findJobSince("ENRICH_CONTACT", ctx.artifacts.enrichStartedAt);
  const turn = api.beginTurn();
  const listName = ctx.artifacts.listName || "your list";
  if (job && job.failed) {
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card(
        "Enrichment failed",
        `The enrichment job for **${listName}** stopped before it finished${job.processed ? `; ${job.processed.toLocaleString()} of ${job.total.toLocaleString()} were done` : ""}. Credits are only spent on details actually found. Try again, or continue without it.`
      ),
      chips: [
        { actionId: "ob_enrich_retry", label: "Try again", style: "primary" },
        { actionId: "ob_enrich_continue", label: "Continue without it" }
      ]
    });
    turn.end(AWAIT.CTA);
    return false; // stay available: they may retry, then check again
  }
  if (job && job.done) {
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card("Enrichment finished", "Your list is ready for the campaign.", [
        { label: "Enriched", value: `${job.processed.toLocaleString()} of ${job.total.toLocaleString()} contacts` }
      ]),
      chips: [{ actionId: "ob_enrich_continue", label: "Continue", style: "primary" }]
    });
    turn.end(AWAIT.CTA);
    return false; // stay available: they may retry, then check again
  }
  turn.addBlock(BLOCK.CTA_CHIPS, {
    ...card(
      "Enrichment still running",
      "Ask me again in a moment, or keep going and I'll report back before we enrol.",
      job ? [{ label: "Progress", value: `${job.processed.toLocaleString()} of ${job.total.toLocaleString()} (${job.percent}%)` }] : []
    ),
    chips: [
      { actionId: "ob_enrich_status", label: "Check enrichment", style: "primary" },
      { actionId: "ob_enrich_continue", label: "Continue while it runs" }
    ]
  });
  turn.end(AWAIT.CTA);
  return false;
}

// Off-script detours can change the account (the consultant saved a list,
// the user did something in another tab). Snapshot what we can compare on
// the way out; on "Continue" adopt what changed instead of replaying a step
// the account has already moved past.
export const beginDetour = async ctx => {
  ctx._detourAt = Date.now();
  if (!ctx.artifacts.listId) {
    ctx._detourLists = await svc.snapshotLists().catch(() => []);
  }
};

// Reload-safe half of reconcileAfterDetour: compares against the snapshot
// persisted with the search, so it survives the page dying mid-detour.
async function adoptListFromSnapshot(api, ctx) {
  const a = ctx.artifacts || {};
  if (a.listId || !a.searchAt || !Array.isArray(a.listsAtSearch)) return null;
  const hit = await svc.findListChangedSince(a.listsAtSearch, a.searchAt).catch(() => null);
  if (!hit || !hit.count) return null;
  ctx.setStep(5);
  ctx.patchArtifacts({ listId: hit.id, listName: hit.name, listSize: hit.count, listsAtSearch: null });
  track(MXP_EVENT.ONBOARDING_LIST_SAVED, { source: "detour" });
  const turn = api.beginTurn();
  await turn.text(
    `**${hit.name}** already has ${hit.count.toLocaleString()} contact${hit.count === 1 ? "" : "s"}, so let's carry on from there.`
  );
  await emitWaitForFill(api, ctx, turn);
  return true;
}

async function reconcileAfterDetour(api, ctx) {
  const a = ctx.artifacts || {};
  // Search done, no list yet: did a list get created or grow during the detour?
  if (ctx.step >= 4 && !a.listId && ctx._detourLists) {
    const hit = await svc.findListChangedSince(ctx._detourLists, ctx._detourAt).catch(() => null);
    ctx._detourLists = null;
    if (hit) {
      ctx.setStep(5);
      ctx.patchArtifacts({ listId: hit.id, listName: hit.name, listSize: hit.count });
      track(MXP_EVENT.ONBOARDING_LIST_SAVED, { source: "detour" });
      const turn = api.beginTurn();
      await turn.text(`I see **${hit.name}** now has ${hit.count.toLocaleString()} contact${hit.count === 1 ? "" : "s"}. Let's continue from that list.`);
      await emitWaitForFill(api, ctx, turn);
      return true;
    }
  }
  return false;
}

// After a consultant answer: hand the mic back explicitly instead of silently
// re-printing the step, which read as the copilot ignoring the question.
export const emitOffScriptBridge = async api => {
  const turn = api.beginTurn();
  turn.addBlock(BLOCK.CTA_CHIPS, {
    ...card("Anything else?", "Ask me another question, or let's continue with your setup."),
    chips: [
      { actionId: "ob_ob_continue", label: "Continue setup", style: "primary" },
      { actionId: "ob_ask_more", label: "Ask another question" }
    ]
  });
  turn.end(AWAIT.CTA);
};

// ── After the sequence is created ────────────────────────────────────────
// Order: list filled? -> enrich card -> add to sequence -> Review and activate.
export async function emitPostSequence(api, ctx) {
  ctx.setStep(9);
  const a = ctx.artifacts || {};
  if (a.listId) {
    const st = await svc.getListFillStatus(a.listId, a.listSize, a.fillStartedAt);
    const plateau = st.added > 0 && st.added === ctx._lastFill;
    ctx._lastFill = st.added;
    if (!(st.done || plateau || st.failed)) {
      const turn = api.beginTurn();
      turn.addBlock(BLOCK.CTA_CHIPS, {
        ...card(
          "Still adding contacts to your list",
          "Your sequence is ready. I'll add the contacts once they've all landed; check back in a moment, or skip ahead and review the sequence now.",
          [{ label: "Added so far", value: `${st.added.toLocaleString()} of ${(st.expected || 0).toLocaleString()}` }]
        ),
        chips: [
          { actionId: "ob_check_fill_post", label: "Check status", style: "primary" },
          { actionId: "ob_skip_to_review", label: "Skip and review the sequence" }
        ]
      });
      turn.end(AWAIT.CTA);
      return undefined;
    }
    if (st.added) ctx.patchArtifacts({ listSize: st.added });
  }
  return emitEnrichStep(api, ctx);
}

// Add the list to the sequence, then hand the user to the sequence page.
async function emitEnrol(api, ctx) {
  const a = ctx.artifacts || {};
  if (!a.listId || !a.sequenceId) return emitFinalCard(api, ctx, { count: 0 });
  const turn = api.beginTurn();
  const res = await runWork(
    api,
    turn,
    {
      label: "Adding your contacts to the sequence",
      messages: ["Reading your list...", "Adding contacts...", "Finishing up..."],
      detail: "Contacts queued"
    },
    svc.enrolListInSequence(a.listId, a.sequenceId)
  ).catch(() => ({ ok: false, count: 0 }));
  ctx.patchArtifacts({ enrolCount: res.count || 0, enrolOk: !!res.ok });
  return emitFinalCard(api, ctx, { count: res.count || 0, failed: !res.ok, turn });
}

// The last card. Leaving for the sequence page is what completes onboarding
// (the chip carries completeOnboarding), so a user who wanders off instead
// simply resumes here.
async function emitFinalCard(api, ctx, { count = 0, failed = false, skipped = false, turn } = {}) {
  const a = ctx.artifacts || {};
  turn = turn || api.beginTurn();
  const review = {
    actionId: LOCAL_ACTIONS.NAVIGATE,
    path: `/sequences/all/${a.sequenceId}`,
    label: "Review and activate",
    style: "primary",
    completeOnboarding: true
  };
  const listName = a.listName ? `**${a.listName}**` : "your list";
  if (skipped) {
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card("Your campaign is ready to review", `I haven't added the contacts yet; add ${listName} from the sequence page once they've landed. Nothing sends until you activate it.`),
      chips: [review]
    });
  } else if (failed) {
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card("Your campaign is ready, but the contacts didn't add", "Open the sequence and add your list from there. Nothing sends until you activate it."),
      chips: [{ ...review, label: "Open sequence" }, { actionId: "ob_check_fill_post", label: "Try adding again" }]
    });
  } else if (!count) {
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card("Your campaign is ready to review", "There were no contacts to add yet. Open the sequence to review it and add contacts from there once your list has some. Nothing sends until you activate it."),
      chips: [{ ...review, label: "Open sequence" }]
    });
  } else {
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card(
        "Your campaign is ready to review",
        `Adding ${count.toLocaleString()} contact${count === 1 ? "" : "s"} from ${listName} to your sequence in the background. Nothing sends until you activate it.`,
        [{ label: "Contacts", value: count.toLocaleString() }]
      ),
      chips: [review]
    });
  }
  turn.end(AWAIT.CTA);
  return undefined;
}

async function emitEnrichStep(api, ctx, turn) {
  turn = turn || api.beginTurn();
  const listName = ctx.artifacts.listName || "";
  const listSize = ctx.artifacts.listSize || 0;
  const credits = await svc.getCredits();
  const remaining = credits.remaining || 0;

  // No credits: do not offer enrich (it would just 409). Explain
  // and let the user top up or skip and continue the flow.
  if (remaining <= 0) {
    turn.addBlock(BLOCK.CTA_CHIPS, {
      ...card(
        "No credits left to enrich",
        `${listName ? `${listName} is saved. ` : ""}Enriching verified emails needs credits and you have 0 left. Top up, or skip and enrich later, then continue setting up your campaign.`,
        [{ label: "Credits left", value: "0" }]
      ),
      chips: [
        { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/pricing", label: "Top up credits", style: "primary" },
        { actionId: "ob_enrich_skip", label: "Skip for now" }
      ]
    });
    turn.end(AWAIT.CTA);
    return;
  }

  // Some credits, but fewer than the list needs: warn, but still allow (it
  // enriches what it can) alongside skip.
  const short = listSize && remaining < listSize;
  const costPhrase = listSize
    ? short
      ? `Enriching by email costs 1 credit each; you have ${remaining.toLocaleString()} credits for ${listSize.toLocaleString()} contacts, so I'll enrich the ones I can.`
      : `Enriching ${listSize.toLocaleString()} contacts by email costs about ${listSize.toLocaleString()} of your ${remaining.toLocaleString()} credits.`
    : `You have ${remaining.toLocaleString()} credits.`;
  turn.addBlock(BLOCK.CTA_CHIPS, {
    ...card(
      "Enrich your list",
      `Your sequence is ready. Before I add ${listName || "your list"} to it, let's get verified emails and phone numbers. ${costPhrase}`,
      [
        { label: "Emails", value: "1 credit each" },
        { label: "Phone numbers", value: "10 credits each" },
        { label: "Credits left", value: remaining.toLocaleString() }
      ]
    ),
    chips: [
      { actionId: "ob_enrich_email", label: "Enrich emails", style: "primary" },
      { actionId: "ob_enrich_both", label: "Emails and phones" },
      { actionId: "ob_enrich_skip", label: "Skip for now" }
    ]
  });
  turn.end(AWAIT.CTA);
}

// Step 7 emitter, shared by the enrich step and resume.
async function emitConnectStep(api, ctx) {
  ctx.setStep(7);
  const status = await svc.refreshConnectStatus();
  ctx.patchArtifacts({ emailConnected: status.email, linkedInConnected: status.linkedIn });
  if (status.email) {
    return emitCampaignStep(api, ctx);
  }
  const turn = api.beginTurn();
  return turn
    .text(
      "Almost there. Connect your email so I can send from your inbox and write in your voice. LinkedIn is optional, it unlocks LinkedIn steps in your campaigns."
    )
    .then(() => {
      turn.addBlock(BLOCK.CTA_CHIPS, {
        title: "Connect your inbox",
        chips: [
          { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/settings/account", label: "Connect email", style: "primary" },
          { actionId: LOCAL_ACTIONS.NAVIGATE, path: "/settings/account", label: "Connect LinkedIn" },
          { actionId: "ob_check_connection", label: "Check connection" },
          { actionId: "ob_connect_later", label: "I'll do this later" }
        ]
      });
      turn.end(AWAIT.CTA);
    });
}

// Step 8 emitter.
async function emitCampaignStep(api, ctx) {
  ctx.setStep(8);
  const status = await svc.refreshConnectStatus();
  const li = status.linkedIn;
  const turn = api.beginTurn();
  const listSize = (ctx.artifacts && ctx.artifacts.listSize) || 0;
  const who = listSize ? `these ${listSize.toLocaleString()} contacts` : "your contacts";
  return turn.text(`Your inbox is connected. How do you want to reach ${who}?`).then(() => {
    // Each card carries its own button (ctaLabel + prompt), so the card is the
    // action. No separate chip row. LinkedIn styles stay visible but gated.
    // Subtitles describe the style, not a promise that steps are pre-written:
    // Step 8 hands off to the build-a-sequence flow, which writes the steps
    // from the seller profile, so the cards describe written steps again.
    turn.addBlock(BLOCK.COMPARISON_GRID, {
      title: "Pick a style",
      options: [
        {
          title: "Email",
          subtitle: "3 email steps, written from your seller profile",
          ctaLabel: "Use email",
          prompt: "Create the email campaign"
        },
        {
          title: "Email + LinkedIn",
          subtitle: li ? "3 email steps plus a profile view and a connection request" : "Connect LinkedIn to use this",
          ctaLabel: li ? "Use email and LinkedIn" : "Connect LinkedIn to use this",
          prompt: li ? "Create the email and LinkedIn campaign" : "Connect LinkedIn to continue"
        },
        {
          title: "Deep Research: Email + LinkedIn",
          subtitle: "I research each contact first, then write every step for that person",
          recommended: true,
          ctaLabel: li ? "Use deep research" : "Connect LinkedIn to use this",
          prompt: li ? "Create the deep research campaign" : "Connect LinkedIn to continue"
        }
      ]
    });
    turn.end(AWAIT.USER_INPUT);
  });
}

// Re-shows the current step's prompt after an off-script interjection, so the
// flow returns to the rail. Re-derives from ctx (step + artifacts); never
// re-runs a mutating service call (steps 2/3 re-show the cached draft, not a
// fresh enrich).
// Where the flow should be, computed from what is actually TRUE rather than
// from the step counter. Replaying the step the user happened to be on is what
// re-offered "Add to list" after a detour had already saved the list; keying on
// artifacts means a detour that did real work simply advances the flow.
// Interruption and resume are the same problem, so both go through here.
// Re-offering the save step after a detour needs the same card the flow shows,
// so both paths share one definition instead of drifting apart.
export const emitSaveListStep = async (api, ctx) => {
  const a = ctx.artifacts || {};
  const total = a.searchTotal || 0;
  const overCap = total > svc.LIST_SAVE_CAP;
  const turn = api.beginTurn();
  turn.addBlock(BLOCK.QUICK_REPLIES, {
    prompt: "Want me to save these as your first list?",
    replyId: "save_list",
    options: [
      { value: "save", label: overCap ? `Add top ${svc.LIST_SAVE_CAP} to list` : "Add to list" },
      { value: "refine", label: "Refine the search first" }
    ]
  });
  turn.end(AWAIT.QUICK_REPLY);
  return undefined;
};

export const emitHandoff = async (api, ctx) => emitPostSequence(api, ctx);

export const advanceFromState = async (api, ctx) => {
  const a = ctx.artifacts || {};
  if (ctx.status !== "active" && ctx.step <= 0) return emitWelcome(api);

  // Furthest-completed wins, in flow order.
  if (a.sequenceId) return emitPostSequence(api, ctx);
  if (a.listId) return a.emailConnected ? emitCampaignStep(api, ctx) : emitConnectStep(api, ctx);
  if (a.searchTotal) {
    // A list may have appeared while we were off the rail (the agent saved
    // one, another tab did). Adopt it instead of replaying the save step.
    const adopted = await adoptListFromSnapshot(api, ctx);
    if (adopted) return adopted;
    // The matched rows live only in the client's search state, so after a
    // reload the table is gone. Re-offering "save these" with nothing on
    // screen meant "add the top 2" re-ran the search and picked a DIFFERENT
    // two people than the ones the user had been looking at. Re-run the same
    // ICP search first so the table, and "these", mean what they did before.
    if (a.icpFilters && !svc.currentSearchTotal()) return runSearchStep(api, ctx, a.icpFilters);
    return emitSaveListStep(api, ctx);
  }
  if (a.savedProfileId || a.draftProfile) {
    // An unapproved draft still needs its review card.
    if (a.draftProfile && !a.savedProfileId) {
      const turn = api.beginTurn();
      await turn.text("Here's your seller profile. Edit any card, or continue.");
      turn.addBlock(BLOCK.SELLER_PROFILE_PREVIEW, { profile: a.draftProfile, done: false });
      turn.end(AWAIT.SELLER_PROFILE_PREVIEW);
      return undefined;
    }
    return a.icpFilters ? emitIcpConfirm(api, ctx) : emitIcpForm(api, ctx);
  }
  if (ctx.step >= 1) return emitSellingQuestion(api, ctx);
  return emitWelcome(api);
};

// Kept as the old name so existing callers keep working.
export const emitCurrentStep = advanceFromState;


export default buildOnboardingScenario;
